class RoWishlist extends HTMLElement {
  constructor() {
    super();
    this.customerId = this.dataset.customerId;
  }

  connectedCallback() {
    this.loadWishlist();

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (cartUpdateData) => {
      const variantId = cartUpdateData?.productVariantId != null ? String(cartUpdateData.productVariantId) : '';
      if (! variantId) return;

      const inputs = this.querySelectorAll('ro-product-form form input[name=id]');
      let wishlistItemToRemove = null;

      for (const input of inputs) {
        if (input.value === variantId) {
          wishlistItemToRemove = input.closest('ro-wishlist-item');
          break;
        }
      }

      if (! wishlistItemToRemove) return;

      this.removeItem(wishlistItemToRemove);
    });
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber && this.cartUpdateUnsubscriber();
  }

  async loadWishlist() {
    try {
      const items = await WishlistUtils.retrieveWishlist({ force: true });
      this.wishlistData = { items };
      this.loadWishlistItems();
    } catch (err) {
      console.log('Error while fetching wishlist:', err);
    }
  }

  async loadWishlistItems() {
    const fetchArray = this.wishlistData.items.map(item =>
      fetch('/products/' + item.handle + '/?sections=ro-wishlist-item&variant=' + item.variant_id)
    );

    if (fetchArray.length === 0) {
      publish(PUB_SUB_EVENTS.wishlistLoaded);
      return;
    }

    try {
      const responses = await Promise.all(fetchArray);
      const jsonResponses = await Promise.all(responses.map(res => res.json()));
      const fragment = document.createDocumentFragment();

      jsonResponses.forEach((response, index) => {
        const html = new DOMParser().parseFromString(response['ro-wishlist-item'], 'text/html');
        const wishlistItem = html.querySelector('ro-wishlist-item');
        const apiItem = this.wishlistData.items[index];

        if (wishlistItem && apiItem) {
          /** Align DOM with API so remove updates the same product+variant as PLP and localStorage. */
          wishlistItem.dataset.productId = String(apiItem.id);
          wishlistItem.dataset.variantId = String(apiItem.variant_id);
          fragment.appendChild(wishlistItem);
        }
      });

      // Prune items no longer existing in Shopify and keep cache + tracking aligned.
      this.wishlistData.items.forEach(item => {
        if (fragment.querySelector(`ro-wishlist-item[data-product-id='${item.id}'][data-variant-id='${item.variant_id}']`) === null) {
          WishlistUtils.removeFromSmartWishlist({ id: item.id, variant_id: item.variant_id });
        }
      });

      this.appendChild(fragment);
      publish(PUB_SUB_EVENTS.wishlistLoaded);
    } catch (error) {
      console.error('Error while loading wishlist items:', error);
    }
  }

  async removeItem(wishlistItem) {
    try {
      if (! wishlistItem.dataset.productId || ! wishlistItem.dataset.variantId) {
        throw new Error('Dataset is missing');
      }

      await WishlistUtils.removeFromSmartWishlist({
        id: wishlistItem.dataset.productId,
        variant_id: wishlistItem.dataset.variantId,
      });

      wishlistItem.remove();
      publish(PUB_SUB_EVENTS.wishlistUpdated);
      publish(PUB_SUB_EVENTS.wishlistChange);
    } catch (error) {
      console.error('Error while removing item from wishlist', error);
    }
  }
}

customElements.define('ro-wishlist', RoWishlist);


class RoWishlistCounter extends HTMLElement {
  constructor() {
    super();
    this.wishlist = this.closest('ro-wishlist');
  }

  connectedCallback() {
    this.unsubscribeLoaded = subscribe(PUB_SUB_EVENTS.wishlistLoaded, () => this.updateCounter());
    this.unsubscribeUpdated = subscribe(PUB_SUB_EVENTS.wishlistUpdated, () => this.updateCounter());
  }

  disconnectedCallback() {
    this.unsubscribeLoaded && this.unsubscribeLoaded();
    this.unsubscribeUpdated && this.unsubscribeUpdated();
  }

  updateCounter() {
    if (! this.wishlist) {
      return;
    }

    const counter = this.wishlist.querySelectorAll('ro-wishlist-item').length;

    this.innerText = "[ " + counter + " ]";

    if (counter > 0) {
      this.wishlist.querySelector('.empty-wishlist')?.classList.add('tw-hidden');
    } else {
      this.wishlist.querySelector('.empty-wishlist')?.classList.remove('tw-hidden');
    }
  }
}

customElements.define('ro-wishlist-counter', RoWishlistCounter);


class RoWishlistItemSizeSelector extends HTMLElement {
  constructor() {
    super();
    this._internals = this.attachInternals();
    this.wishlistItem = this.closest('ro-wishlist-item');
    this.selectedSizeName = this.querySelector('.js-selected-size-name');
  }

  /* STATES */
  get open() { return this._internals.states.has("open"); }

  set open(flag) {
    if (flag) {
      this._internals.states.add("open");
      this.documentClickListenerAbortController = new AbortController();

      document.addEventListener(
        'click',
        (event) => this.handleOutsideClick(event),
        {
          'signal': this.documentClickListenerAbortController.signal
        }
      );
    } else {
      this._internals.states.delete("open");
      this.documentClickListenerAbortController.abort();
    }
  }

  toggleOpen() { this.open ? this.open = false : this.open = true; }

  /*
    <style>
      fake-select:state(open){}
    </style>
  */

  connectedCallback() {
    if (! this.wishlistItem || ! this.selectedSizeName) {
      return;
    }

    this.addEventListener('click', (event) => {
      event.preventDefault();

      if (event.target.matches("label")) {
        this.handleClick(event.target);
      } else {
        this.toggleOpen();
      }
    });
  }

  handleClick(selectedItemLabel) {
    const selectedItemLi = selectedItemLabel.closest('li');

    if (selectedItemLi.dataset.available !== "true") {
      return;
    }

    try {
      if (! this.wishlistItem.dataset.productId || ! this.wishlistItem.dataset.variantId) {
        throw new Error('Dataset is missing');
      }

      if (! selectedItemLi.dataset.variantId || ! selectedItemLi.dataset.variantMaxQtyPerOrder) {
        throw new Error('Target dataset is missing');
      }

      this.selectedSizeName.innerText = selectedItemLabel.innerText;
      this.selectedSizeName.classList.remove('tw-line-through');

      this.open = false;

      publish(PUB_SUB_EVENTS.wishlistItemSizeUpdated, {
        productId: this.wishlistItem.dataset.productId,
        originalVariantId: this.wishlistItem.dataset.variantId,
        selectedVariantId: selectedItemLi.dataset.variantId,
        selectedVariantMaxQtyPerOrder: selectedItemLi.dataset.variantMaxQtyPerOrder
      });
    } catch (error) {
      console.error('Error while updating wishlist item size', error);
    }
  }

  handleOutsideClick(event) {
    if (!this.contains(event.target) && this.open === true) {
      this.toggleOpen();
    }
  }
}

customElements.define('ro-wishlist-item-size-selector', RoWishlistItemSizeSelector);


class RoWishlistItemRemoveButton extends HTMLElement {
  constructor() {
    super();
    this._onRemoveClick = () => {
      if (this.wishlist && this.wishlistItem) {
        this.wishlist.removeItem(this.wishlistItem);
      }
    };
  }

  connectedCallback() {
    this.button?.removeEventListener('click', this._onRemoveClick);

    this.wishlistItem = this.closest('ro-wishlist-item');
    this.wishlist = this.closest('ro-wishlist');
    this.button = this.querySelector('button');

    if (! this.wishlistItem || ! this.wishlist || ! this.button) {
      return;
    }

    this.button.addEventListener('click', this._onRemoveClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this._onRemoveClick);
  }
}

customElements.define('ro-wishlist-item-remove-button', RoWishlistItemRemoveButton);


class RoWishlistItemAddToCartButton extends HTMLElement {
  constructor() {
    super();
    this.wishlistItem = this.closest('ro-wishlist-item');
    this.roProductForm = this.querySelector('ro-product-form');
    this.form = this.roProductForm.querySelector('form');
  }

  connectedCallback() {
    if (! this.wishlistItem || ! this.roProductForm || ! this.form) {
      return;
    }

    this.unsubscribeSizeUpdated = subscribe(PUB_SUB_EVENTS.wishlistItemSizeUpdated, (sizeUpdateData) => {
      if (! sizeUpdateData) return;
      if (this.wishlistItem.dataset.productId !== sizeUpdateData.productId || this.wishlistItem.dataset.variantId !== sizeUpdateData.originalVariantId) { // If this item is not the event target
        return;
      }

      this.form.maxqty.value = sizeUpdateData.selectedVariantMaxQtyPerOrder;
      this.roProductForm.variantIdInput.value = sizeUpdateData.selectedVariantId;
      this.roProductForm.submitButton.disabled = false;
    });

    this.unsubscribeCartError = subscribe(PUB_SUB_EVENTS.cartError, (cartErrorData) =>{
      if (! cartErrorData) return;
      if (this.form.id.value !== cartErrorData.productVariantId) {
        return;
      }

      this.notifyVariantOrderMaxQtyReached();
    });

    this.unsubscribeMaxQtyReached = subscribe(PUB_SUB_EVENTS.variantOrderMaxQtyReached, (eventData) => {
      if (! eventData) return;
      if (this.form.id.value !== eventData.variant) {
        return;
      }

      this.notifyVariantOrderMaxQtyReached();
    });
  }

  disconnectedCallback() {
    this.unsubscribeSizeUpdated && this.unsubscribeSizeUpdated();
    this.unsubscribeCartError && this.unsubscribeCartError();
    this.unsubscribeMaxQtyReached && this.unsubscribeMaxQtyReached();
  }

  notifyVariantOrderMaxQtyReached() {
    const maxQuantityError = this.wishlistItem.querySelector('.js-max-quantity-error');

    if (maxQuantityError === null) {
      return;
    }

    maxQuantityError.classList.remove('tw-hidden');

    setTimeout(() => {
      maxQuantityError.classList.add('tw-hidden');
    }, 5000);
  }
}

customElements.define('ro-wishlist-item-add-to-cart-button', RoWishlistItemAddToCartButton);